import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MDContentBoxComponent, MDIconComponent } from '@melodic-ui/components';
import { IInstructorAvailabilityBlockSummary } from '@lb/api/mylb/interfaces/iinstructor-availability-block-summary.interface';
import { differenceInHours } from 'date-fns';

@Component({
	selector: 'lb-availability-summary',
	standalone: true,
	imports: [CommonModule, MDContentBoxComponent, MDIconComponent],
	templateUrl: './availability-summary.component.html',
	styleUrl: './availability-summary.component.scss'
})
export class AvailabilitySummaryComponent {
	// Inputs
	summaries = input.required<IInstructorAvailabilityBlockSummary[] | undefined>();
	type = input.required<'planned' | 'flex' | 'unavailable'>();
	title = input<string>();
	showTitle = input<boolean>(true);
	displayMode = input<'standalone' | 'inline'>('standalone');

	// Outputs
	summaryClicked = output<IInstructorAvailabilityBlockSummary>();

	// Computed display title
	displayTitle = computed(() => {
		const customTitle = this.title();
		if (customTitle) return customTitle;

		const type = this.type();
		switch (type) {
			case 'planned':
				return 'Planned Availability';
			case 'flex':
				return 'Makeup Availability';
			case 'unavailable':
				return 'Unavailability';
			default:
				return '';
		}
	});

	// Time display logic
	timeDisplayForSummary(summary: IInstructorAvailabilityBlockSummary): string {
		let hourUnit = 'hours';
		if (summary.totalHoursPerWeek === 1) {
			hourUnit = 'hour';
		}

		let viewPerWeek = true;
		if (summary.endDate) {
			if (summary.availabilityTypeID === 3) {
				// Track day/hours based on time range for unavailability
				const startDate = new Date(summary.startDate);
				const endDate = new Date(summary.endDate);
				const totalHours = differenceInHours(endDate, startDate, { roundingMethod: 'round' });

				const days = Math.floor(totalHours / 24);
				const dayUnit = days === 1 ? 'day' : 'days';
				const hours = totalHours % 24;

				let displayText = '';
				if (days > 0) {
					displayText += `${days} ${dayUnit}`;
				}

				if (hours > 0) {
					const hourText = hours === 1 ? 'hour' : 'hours';
					displayText += `${displayText ? ' ' : ''}${hours} ${hourText}`;
				}

				return displayText;
			}

			// hours/week if it is greater than 7 days, otherwise show total hours in block
			const startDate = new Date(summary.startDate);
			const endDate = new Date(summary.endDate);
			const daysDifference = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24));
			if (daysDifference <= 7) {
				viewPerWeek = false;
			}
		}

		return `${summary.totalHoursPerWeek} ${hourUnit}${viewPerWeek ? '/week' : ''}`;
	}

	handleCardClick(summary: IInstructorAvailabilityBlockSummary): void {
		this.summaryClicked.emit(summary);
	}
}
